import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, CloudUpload, DatabaseBackup, Download, FileText, HardDrive, Pencil, Play, Plus, Power, PowerOff, RotateCcw, ShieldCheck, Trash2 } from 'lucide-react';
import type { BackupBrowseDatabasesResponse, BackupBrowseEntry, BackupBrowseFoldersResponse, BackupPlan, BackupRun, BackupSourceType, Resource, StorageTarget } from '@itr/shared';
import { backupSourceLabel, formatBytes, formatDuration, formatRelative, planSources, sourceDatabases } from '@itr/shared';
import { api, apiDownload } from '../api/client';
import { useRealtimeEvent } from '../api/realtime';
import { useAuth } from '../auth/AuthProvider';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useFetch,
  useSelection,
  useToast,
} from '../components/ui';

export type PlanRow = BackupPlan & { lastSuccessAt: number | null; rpoSec: number | null; lastSize: number | null };
export type RunRow = BackupRun & { planName: string };

export function BackupsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState<'plans' | 'runs' | 'storage'>('plans');
  const [planOpen, setPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanRow | null>(null);
  const [targetOpen, setTargetOpen] = useState(false);
  const [restoringRun, setRestoringRun] = useState<RunRow | null>(null);
  const [logRun, setLogRun] = useState<RunRow | null>(null);

  const plans = useFetch(() => api.get<PlanRow[]>('/api/plugins/backups/plans'), [], { refreshMs: 30_000 });
  const runs = useFetch(() => api.get<RunRow[]>('/api/plugins/backups/runs'), [], { refreshMs: 15_000 });
  const targets = useFetch(() => api.get<StorageTarget[]>('/api/plugins/backups/targets'), []);

  // The runner announces start and finish over the socket; the polls above are
  // only a fallback, so a run flips to its final state as soon as it ends.
  useRealtimeEvent(['backups'], (event) => {
    if (event.type === 'backup.started' || event.type === 'backup.finished') {
      runs.reload();
      plans.reload();
    }
  });

  const runNow = async (plan: PlanRow) => {
    try {
      await api.post(`/api/plugins/backups/plans/${plan.id}/run`);
      toast(`Backup "${plan.name}" started`, 'good', 'Progress appears under Runs.');
      setTimeout(() => runs.reload(), 1500);
    } catch (err) {
      toast('Could not start the backup', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const overdue = (plans.data ?? []).filter((p) => p.enabled && (p.rpoSec === null || p.rpoSec > 36 * 3600));

  const canRun = can('backup:run');
  const canWritePlans = can('backup:write');
  const planSelection = useSelection((plans.data ?? []).map((p) => p.id));

  const bulkRunNow = async () => {
    const result = await runBulk(planSelection.selectedIds, (id) => api.post(`/api/plugins/backups/plans/${id}/run`));
    const { message, intent } = bulkResultMessage('Started', result);
    toast(message, intent, 'Progress appears under Runs.');
    planSelection.clear();
    setTimeout(() => runs.reload(), 1500);
  };

  const bulkDeletePlans = async () => {
    if (!confirm(`Delete ${planSelection.count} backup plan(s)? Existing archives are left in place.`)) return;
    const result = await runBulk(planSelection.selectedIds, (id) => api.delete(`/api/plugins/backups/plans/${id}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    planSelection.clear();
    plans.reload();
  };

  const deletePlan = async (plan: PlanRow) => {
    if (!confirm(`Delete "${plan.name}"? Existing archives are left in place.`)) return;
    await api.delete(`/api/plugins/backups/plans/${plan.id}`);
    toast('Backup plan deleted', 'good');
    plans.reload();
  };

  const toggleEnabled = async (plan: PlanRow) => {
    await api.post('/api/plugins/backups/plans', { ...plan, enabled: !plan.enabled });
    plans.reload();
    toast(plan.enabled ? 'Backup plan disabled' : 'Backup plan enabled', 'good');
  };

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Backups & storage</h1>
          <div className="page-subtitle">
            {(plans.data ?? []).length} plan{(plans.data ?? []).length === 1 ? '' : 's'} ·{' '}
            {overdue.length > 0 ? `${overdue.length} without a current recovery point` : 'all recovery points current'}
          </div>
        </div>
        <div className="row">
          {can('backup:write') && (
            <>
              <button className="btn" onClick={() => setTargetOpen(true)}>
                <HardDrive size={15} /> Add storage
              </button>
              <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>
                <Plus size={15} /> New plan
              </button>
            </>
          )}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 4 }}>
        {(
          [
            ['plans', 'Plans'],
            ['runs', 'Runs'],
            ['storage', 'Storage targets'],
          ] as const
        ).map(([value, label]) => (
          <button key={value} className={`btn btn-sm${tab === value ? ' btn-primary' : ''}`} onClick={() => setTab(value)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'plans' && (
        <>
          <AutoBackupCard targets={targets.data ?? []} onChanged={() => { plans.reload(); targets.reload(); }} />
          {(canRun || canWritePlans) && (
            <SelectionBar selection={planSelection}>
              {canRun && (
                <button className="btn btn-sm" onClick={bulkRunNow}>
                  <Play size={12} /> Run now
                </button>
              )}
              {canWritePlans && (
                <button className="btn btn-sm btn-danger" onClick={bulkDeletePlans}>
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </SelectionBar>
          )}
          <div className="card">
          {plans.loading && !plans.data ? (
            <div style={{ padding: 16 }}>
              <SkeletonTable rows={5} cols={5} />
            </div>
          ) : plans.error ? (
            <ErrorState error={plans.error} onRetry={plans.reload} />
          ) : (plans.data ?? []).length === 0 ? (
            <EmptyState
              icon={<DatabaseBackup size={20} />}
              title="No backup plans"
              text="Create a plan to schedule backups of files, databases or device configuration to a storage target."
              action={
                can('backup:write') ? (
                  <button className="btn btn-primary" onClick={() => setPlanOpen(true)}>
                    <Plus size={15} /> Create a plan
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {(canRun || canWritePlans) && (
                      <th style={{ width: 40 }}>
                        <SelectAllCheckbox selection={planSelection} />
                      </th>
                    )}
                    <th>Plan</th>
                    <th style={{ width: 110 }}>Source</th>
                    <th style={{ width: 120 }}>Schedule</th>
                    <th style={{ width: 110 }}>Last result</th>
                    <th style={{ width: 140 }}>Recovery point</th>
                    <th data-align="right" style={{ width: 100 }}>
                      Size
                    </th>
                    <th style={{ width: 130 }} />
                  </tr>
                </thead>
                <tbody>
                  {(plans.data ?? []).map((plan) => {
                    // RPO age is the number that matters: a plan whose last good
                    // run was three days ago is broken even if it "succeeded".
                    const stale = plan.rpoSec === null || plan.rpoSec > 36 * 3600;
                    return (
                      <tr key={plan.id}>
                        {(canRun || canWritePlans) && (
                          <td>
                            <RowCheckbox selection={planSelection} id={plan.id} />
                          </td>
                        )}
                        <td>
                          <div style={{ fontWeight: 550 }}>{plan.name}</div>
                          <div className="row" style={{ gap: 4, marginTop: 3 }}>
                            {!plan.enabled && (
                              <span className="badge" style={{ height: 17, fontSize: 10.5 }}>
                                disabled
                              </span>
                            )}
                            {plan.autoSource && (
                              <span className="badge" style={{ height: 17, fontSize: 10.5 }} title="Created by automatic system backups">
                                automatic
                              </span>
                            )}
                            {plan.offsiteTargetId && (
                              <span className="badge" style={{ height: 17, fontSize: 10.5 }} title={`Also copied to ${targetName(targets.data, plan.offsiteTargetId)}`}>
                                <CloudUpload size={10} style={{ marginRight: 3 }} />
                                offsite
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                            {planSources(plan).map((source, i) => (
                              <Badge key={i}>{planSources(plan).length > 1 || source.type === 'files' ? backupSourceLabel(source) : source.type}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="mono subtle" style={{ fontSize: 12 }}>
                          {plan.scheduleCron}
                        </td>
                        <td>
                          {plan.lastStatus ? (
                            <Badge intent={autoIntent(plan.lastStatus)}>{plan.lastStatus}</Badge>
                          ) : (
                            <span className="subtle">never run</span>
                          )}
                        </td>
                        <td style={{ color: stale ? 'var(--itr-critical)' : undefined, fontSize: 12.5 }}>
                          {plan.rpoSec === null ? 'none' : `${formatDuration(plan.rpoSec)} old`}
                        </td>
                        <td data-align="right" className="nums subtle">
                          {plan.lastSize ? formatBytes(plan.lastSize) : '—'}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 2 }}>
                            {can('backup:run') && (
                              <button
                                className="btn btn-ghost btn-icon btn-sm"
                                onClick={() => void runNow(plan)}
                                title="Run now"
                                aria-label="Run now"
                              >
                                <Play size={13} />
                              </button>
                            )}
                            {canWritePlans && (
                              <>
                                <button
                                  className="btn btn-ghost btn-icon btn-sm"
                                  onClick={() => void toggleEnabled(plan)}
                                  title={plan.enabled ? 'Disable' : 'Enable'}
                                  aria-label={plan.enabled ? 'Disable' : 'Enable'}
                                >
                                  {plan.enabled ? <PowerOff size={13} /> : <Power size={13} />}
                                </button>
                                <button
                                  className="btn btn-ghost btn-icon btn-sm"
                                  onClick={() => setEditingPlan(plan)}
                                  title="Edit plan"
                                  aria-label="Edit plan"
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="btn btn-ghost btn-icon btn-sm"
                                  onClick={() => void deletePlan(plan)}
                                  title="Delete plan"
                                  aria-label="Delete plan"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </>
      )}

      {tab === 'runs' && (
        <div className="card">
          {(runs.data ?? []).length === 0 ? (
            <EmptyState title="No runs yet" text="Backup runs will appear here once a plan executes." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th style={{ width: 110 }}>Result</th>
                    <th style={{ width: 150 }}>Started</th>
                    <th style={{ width: 100 }}>Duration</th>
                    <th data-align="right" style={{ width: 100 }}>
                      Size
                    </th>
                    <th>Detail</th>
                    <th style={{ width: 180 }} />
                  </tr>
                </thead>
                <tbody>
                  {(runs.data ?? []).map((run) => (
                    <tr key={run.id}>
                      <td style={{ fontWeight: 540 }}>{run.planName}</td>
                      <td>
                        <div className="row" style={{ gap: 7 }}>
                          <Badge intent={autoIntent(run.state)}>{run.state}</Badge>
                          {run.state === 'running' && (
                            <span
                              style={{
                                width: 34,
                                height: 4,
                                borderRadius: 2,
                                background: 'var(--itr-surface-sunken)',
                                overflow: 'hidden',
                                display: 'inline-block',
                              }}
                            >
                              <span
                                style={{
                                  display: 'block',
                                  height: '100%',
                                  width: `${run.progressPct}%`,
                                  background: 'var(--itr-primary)',
                                  transition: 'width 400ms',
                                }}
                              />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="subtle" style={{ fontSize: 12 }}>
                        {formatRelative(run.startedAt)}
                      </td>
                      <td className="nums subtle">
                        {run.endedAt ? formatDuration((run.endedAt - run.startedAt) / 1000) : '—'}
                      </td>
                      <td data-align="right" className="nums">
                        {run.bytesWritten ? formatBytes(run.bytesWritten) : '—'}
                      </td>
                      <td className="subtle truncate" style={{ maxWidth: 300, fontSize: 12 }}>
                        {run.error ? (
                          <button
                            className="run-error-link truncate"
                            onClick={() => setLogRun(run)}
                            title="Show the full log for this run"
                          >
                            {run.error}
                          </button>
                        ) : run.checksum ? (
                          `verified · ${run.checksum.slice(0, 12)}…`
                        ) : (
                          ''
                        )}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <RunLogButton run={run} onOpen={setLogRun} />
                          {run.state === 'success' && can('backup:restore') && (
                            <>
                              <DownloadRunButton run={run} />
                              <button className="btn btn-sm" onClick={() => setRestoringRun(run)}>
                                <RotateCcw size={12} /> Restore
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <RunLogDrawer run={logRun} onClose={() => setLogRun(null)} />

      <Drawer open={Boolean(restoringRun)} onClose={() => setRestoringRun(null)} title={`Restore — ${restoringRun?.planName ?? ''}`}>
        {restoringRun && (
          <RestoreDialog
            run={restoringRun}
            onDone={() => {
              setRestoringRun(null);
              runs.reload();
            }}
          />
        )}
      </Drawer>

      {tab === 'storage' && (
        <div className="grid-auto">
          {(targets.data ?? []).length === 0 ? (
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <EmptyState
                icon={<HardDrive size={20} />}
                title="No storage targets"
                text="Add a local path, SFTP host, SMB share or S3 bucket for backups to land in."
              />
            </div>
          ) : (
            (targets.data ?? []).map((target) => {
              const usedPct =
                target.capacityBytes && target.usedBytes ? (target.usedBytes / target.capacityBytes) * 100 : null;
              return (
                <div className="card" key={target.id}>
                  <div className="card-header">
                    <HardDrive size={15} />
                    <span className="card-title">{target.name}</span>
                    <span className="grow" />
                    <Badge intent={target.reachable ? 'good' : 'bad'}>{target.reachable ? 'online' : 'unreachable'}</Badge>
                  </div>
                  <div className="card-body">
                    <div className="row" style={{ marginBottom: 8 }}>
                      <Badge>{target.type}</Badge>
                      <span className="grow" />
                      <span className="subtle nums" style={{ fontSize: 12 }}>
                        {target.usedBytes !== null ? formatBytes(target.usedBytes) : '—'}
                        {target.capacityBytes ? ` / ${formatBytes(target.capacityBytes)}` : ''}
                      </span>
                    </div>
                    {usedPct !== null && (
                      <>
                        <div className="bar-track" style={{ height: 7 }}>
                          <div
                            className="bar-fill"
                            style={{
                              width: `${Math.min(100, usedPct)}%`,
                              background:
                                usedPct >= 95 ? 'var(--itr-critical)' : usedPct >= 85 ? 'var(--itr-warning)' : 'var(--itr-success)',
                            }}
                          />
                        </div>
                        <div className="subtle" style={{ fontSize: 11.5, marginTop: 5 }}>
                          {usedPct.toFixed(0)}% used
                          {target.lastCheckedAt && ` · checked ${formatRelative(target.lastCheckedAt)}`}
                        </div>
                      </>
                    )}
                    {target.lastError && (
                      <div style={{ color: 'var(--itr-critical)', fontSize: 12, marginTop: 8 }}>{target.lastError}</div>
                    )}
                    {can('backup:write') && (
                      <div className="row" style={{ marginTop: 12 }}>
                        <button
                          className="btn btn-sm"
                          onClick={async () => {
                            await api.post(`/api/plugins/backups/targets/${target.id}/test`);
                            targets.reload();
                            toast('Storage target tested', 'good');
                          }}
                        >
                          <ShieldCheck size={13} /> Test
                        </button>
                        <span className="grow" />
                        <button
                          className="btn btn-sm btn-icon"
                          onClick={async () => {
                            if (!confirm(`Remove "${target.name}"? Existing archives are left in place.`)) return;
                            await api.delete(`/api/plugins/backups/targets/${target.id}`);
                            targets.reload();
                          }}
                          aria-label="Remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <Drawer open={planOpen} onClose={() => setPlanOpen(false)} title="New backup plan">
        <PlanForm
          targets={targets.data ?? []}
          onSaved={() => {
            setPlanOpen(false);
            plans.reload();
            toast('Backup plan created', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(editingPlan)} onClose={() => setEditingPlan(null)} title={`Edit — ${editingPlan?.name ?? ''}`}>
        {editingPlan && (
          <PlanForm
            targets={targets.data ?? []}
            plan={editingPlan}
            onSaved={() => {
              setEditingPlan(null);
              plans.reload();
              toast('Backup plan updated', 'good');
            }}
          />
        )}
      </Drawer>

      <Drawer open={targetOpen} onClose={() => setTargetOpen(false)} title="Add storage target">
        <TargetForm
          onSaved={() => {
            setTargetOpen(false);
            targets.reload();
            toast('Storage target added', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

export function RunLogButton({ run, onOpen }: { run: RunRow; onOpen: (run: RunRow) => void }) {
  const failed = run.state === 'failed';
  return (
    <button
      className="btn btn-sm btn-icon"
      data-intent={failed ? 'bad' : undefined}
      onClick={() => onOpen(run)}
      title={failed ? 'Why did this fail? Show the log' : 'Show the log'}
      aria-label="Show run log"
    >
      <FileText size={12} />
    </button>
  );
}

/**
 * One run's outcome and full log. Fetched fresh on open. While the run is
 * still going the route answers with the lines written so far, so the drawer
 * polls it every couple of seconds and the log grows live; the poll stops on
 * the first answer in a final state, which carries the saved log.
 */
export function RunLogDrawer({ run, onClose }: { run: RunRow | null; onClose: () => void }) {
  const [live, setLive] = useState(false);
  const detail = useFetch(
    () => (run ? api.get<BackupRun>(`/api/plugins/backups/runs/${run.id}`) : Promise.resolve(null)),
    [run?.id],
    { refreshMs: live ? 2_000 : undefined },
  );
  // useFetch keeps the previous run's data until the new request lands.
  const current = detail.data && detail.data.id === run?.id ? detail.data : run;
  const running = current?.state === 'running' || current?.state === 'verifying' || current?.state === 'queued';

  useEffect(() => {
    setLive(Boolean(run) && running);
  }, [run?.id, running]);

  // The runner announces the finish over the socket; fetch the saved log at
  // once instead of waiting for the next poll.
  useRealtimeEvent(['backups'], (event) => {
    if (event.type === 'backup.finished' && event.runId === run?.id) detail.reload();
  });

  // Follow the log as it grows, unless the reader has scrolled up to look at
  // something — then leave their place alone until they return to the end.
  const logRef = useRef<HTMLPreElement>(null);
  const stickRef = useRef(true);
  const onLogScroll = () => {
    const el = logRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
  };
  useEffect(() => {
    stickRef.current = true;
  }, [run?.id]);
  useEffect(() => {
    const el = logRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [current?.log]);

  return (
    <Drawer open={Boolean(run)} onClose={onClose} title={`Backup log — ${run?.planName ?? ''}`} wide>
      {run && current && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
            <Badge intent={autoIntent(current.state)}>{current.state}</Badge>
            <span className="subtle">
              {new Date(current.startedAt).toLocaleString()} · {current.trigger}
            </span>
            {current.endedAt && (
              <span className="subtle nums">took {formatDuration((current.endedAt - current.startedAt) / 1000)}</span>
            )}
            {current.bytesWritten > 0 && <span className="subtle nums">{formatBytes(current.bytesWritten)}</span>}
          </div>

          {current.error && (
            <div className="run-log-error" role="alert">
              {current.error}
            </div>
          )}

          {current.artifactPath && (
            <div style={{ fontSize: 12 }}>
              <span className="subtle">Stored at </span>
              <span className="mono" style={{ wordBreak: 'break-all' }}>
                {current.artifactPath}
              </span>
              {current.checksum && (
                <div className="subtle mono" style={{ marginTop: 4, wordBreak: 'break-all' }}>
                  sha256 {current.checksum}
                </div>
              )}
            </div>
          )}

          {detail.error && !current.log ? (
            <ErrorState error={`Could not load this run's log: ${detail.error}`} onRetry={detail.reload} />
          ) : current.log ? (
            <pre ref={logRef} onScroll={onLogScroll} className="run-log mono" aria-live={running ? 'polite' : undefined}>
              {current.log}
            </pre>
          ) : (
            <div className="subtle" style={{ fontSize: 13 }}>
              {running ? 'This run is still going — waiting for its first log line…' : 'No log was recorded for this run.'}
            </div>
          )}
          {running && current.log && (
            <div className="subtle row" style={{ fontSize: 12, gap: 6 }}>
              <span className="spinner" style={{ width: 12, height: 12 }} aria-hidden /> Live — updates every 2 seconds while the run is going.
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

/**
 * Fetches a run's archive as stored on its target. Remote targets are pulled
 * to the server first, so the spinner can sit for a while before the save starts.
 */
export function DownloadRunButton({ run }: { run: RunRow }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      await apiDownload(`/api/plugins/backups/runs/${run.id}/download`, `${run.planName}-${run.id}`);
    } catch (err) {
      toast('Download failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      className="btn btn-sm btn-icon"
      onClick={download}
      disabled={busy}
      title={busy ? 'Preparing download…' : 'Download archive'}
      aria-label="Download archive"
    >
      <Download size={12} />
    </button>
  );
}

interface RestorePreview {
  description: string;
  requiresConfirmation: string | null;
}

/**
 * Preview-then-apply, matching the same split device provisioning uses: the
 * server is asked what restoring would do before it is asked to do it, and a
 * destructive kind (overwriting a live database, replaying config onto a live
 * device) requires typing the exact confirmation string back — a click alone
 * is not enough for something this hard to undo.
 */
export function RestoreDialog({ run, onDone }: { run: BackupRun & { planName: string }; onDone: () => void }) {
  const toast = useToast();
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ state: string; message: string; log: string } | null>(null);

  useEffect(() => {
    api
      .post<{ preview: RestorePreview }>(`/api/plugins/backups/runs/${run.id}/restore`, { apply: false })
      .then((res) => setPreview(res.preview))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [run.id]);

  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ state: string; message: string; log: string }>(`/api/plugins/backups/runs/${run.id}/restore`, {
        apply: true,
        confirm: confirmText || undefined,
      });
      setResult(res);
      if (res.state === 'success') {
        toast('Restore complete', 'good', res.message);
        onDone();
      } else {
        toast('Restore failed', 'bad', res.message);
      }
    } catch (err) {
      toast('Restore failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div style={{ color: 'var(--itr-critical)', fontSize: 13 }}>{error}</div>;
  if (!preview) return <div className="subtle" style={{ fontSize: 13 }}>Checking what this run can be restored to…</div>;

  const ready = !preview.requiresConfirmation || confirmText === preview.requiresConfirmation;

  return (
    <div>
      <p style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 16 }}>{preview.description}</p>

      {preview.requiresConfirmation && (
        <div className="field">
          <label className="field-label">
            Type <span className="mono">{preview.requiresConfirmation}</span> to confirm
          </label>
          <input className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus />
        </div>
      )}

      <button className="btn btn-primary" style={{ width: '100%' }} disabled={!ready || busy} onClick={apply}>
        {busy ? 'Restoring…' : 'Restore'}
      </button>

      {result && (
        <div
          className="mono"
          style={{
            marginTop: 14,
            padding: 10,
            fontSize: 11.5,
            whiteSpace: 'pre-wrap',
            background: 'var(--itr-surface-sunken)',
            borderRadius: 'var(--itr-radius-sm)',
            color: result.state === 'success' ? undefined : 'var(--itr-critical)',
          }}
        >
          {result.log}
        </div>
      )}
    </div>
  );
}

function targetName(targets: StorageTarget[] | null | undefined, id: string | null): string {
  if (!id) return 'none';
  const target = targets?.find((t) => t.id === id);
  return target ? `${target.name} (${target.type})` : 'a removed target';
}

interface AutoStatus {
  enabled: boolean;
  targetId: string | null;
  offsiteTargetId: string | null;
  cron: string;
  plans: string[];
  dismissed: string[];
}

/**
 * Automatic system backups: this dashboard's own database and every
 * MagnusBilling server, each nightly, each with an offsite copy once one is
 * picked. The pickers write the backups plugin's settings (plugin:manage);
 * everyone else sees the state read-only.
 */
function AutoBackupCard({ targets, onChanged }: { targets: StorageTarget[]; onChanged: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const auto = useFetch(() => api.get<AutoStatus>('/api/plugins/backups/auto'), []);
  const [busy, setBusy] = useState(false);
  const canManage = can('plugin:manage');

  if (!auto.data) return null;
  const a = auto.data;

  const save = async (patch: Record<string, unknown>, recreateDeleted = false) => {
    setBusy(true);
    try {
      if (Object.keys(patch).length > 0) await api.put('/api/plugins/backups/settings', patch);
      // Sweep now rather than in up to 10 minutes, so the effect is visible.
      await api.post(`/api/plugins/backups/auto/${recreateDeleted ? 'reset' : 'sweep'}`);
      auto.reload();
      onChanged();
      toast('Automatic backups updated', 'good');
    } catch (err) {
      toast('Could not update automatic backups', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const offsiteMissing = a.enabled && !a.offsiteTargetId;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <ShieldCheck size={14} />
        <span className="card-title">Automatic system backups</span>
        <span className="grow" />
        {canManage && (
          <label className="row" style={{ gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={a.enabled} disabled={busy} onChange={(e) => void save({ autoSystemBackup: e.target.checked })} />
            On
          </label>
        )}
      </div>
      <div className="card-body" style={{ fontSize: 12.5 }}>
        <p className="subtle" style={{ marginTop: 0, lineHeight: 1.6 }}>
          {a.enabled ? (
            <>
              This dashboard's database and every MagnusBilling server with an SSH login are backed up on{' '}
              <span className="mono">{a.cron}</span> — {a.plans.length} automatic plan{a.plans.length === 1 ? '' : 's'} so far.
              Deleting one stops it being recreated.
            </>
          ) : (
            'Off — no automatic plans are created. Existing ones keep running until you disable or delete them.'
          )}
        </p>
        {offsiteMissing && (
          <p style={{ color: 'var(--itr-warning)', lineHeight: 1.6 }}>
            No offsite target is set, so these backups live only on {targetName(targets, a.targetId) === 'none' ? 'this server' : targetName(targets, a.targetId)}.
            Pick an offsite target so losing this server doesn't lose its backups too.
          </p>
        )}
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
            <label className="field-label">Primary target (for new automatic plans)</label>
            <select
              className="select"
              value={a.targetId ?? ''}
              disabled={!canManage || busy}
              onChange={(e) => void save({ autoTargetId: e.target.value })}
            >
              <option value="">Local folder on this server</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.type})
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
            <label className="field-label">Offsite copy</label>
            <select
              className="select"
              value={a.offsiteTargetId ?? ''}
              disabled={!canManage || busy}
              onChange={(e) => void save({ offsiteTargetId: e.target.value })}
            >
              <option value="">None</option>
              {targets
                .filter((t) => t.id !== a.targetId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.type})
                  </option>
                ))}
            </select>
          </div>
        </div>
        {canManage && a.dismissed.length > 0 && (
          <button className="btn btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={() => void save({}, true)}>
            <RotateCcw size={12} /> Recreate {a.dismissed.length} deleted automatic plan{a.dismissed.length === 1 ? '' : 's'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A config plan's sourceConfig with the login from the editor applied. A
 * blank password keeps the stored one; typing one replaces the plan's saved
 * vault credential (which would otherwise win at run time) with these values.
 */
function withConfigLogin(source: Record<string, unknown>, username: string, password: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...source, username: username || undefined };
  if (password) {
    next.password = password;
    delete next.vaultEntryId;
  }
  return next;
}

/* ---- One source in the plan form ---- */

interface SourceDraft {
  key: number;
  type: BackupSourceType;
  /** What the form has no field for (vendor, exclude, vault entry) survives a save. */
  config: Record<string, unknown>;
  paths: string[];
  databases: string[];
  host: string;
  username: string;
  password: string;
}

let sourceKey = 0;
function draftOf(type: BackupSourceType, config: Record<string, unknown>): SourceDraft {
  const isDb = type === 'mysql' || type === 'postgres';
  return {
    key: ++sourceKey,
    type,
    config,
    paths: type === 'files' ? ((config.paths as string[] | undefined) ?? []) : [],
    databases: isDb ? sourceDatabases(config) : [],
    host: isDb ? String(config.host ?? '') : '',
    username: isDb ? String(config.username ?? '') : '',
    password: isDb ? String(config.password ?? '') : '',
  };
}

function sourceConfigOf(draft: SourceDraft): Record<string, unknown> {
  if (draft.type === 'files') return { ...draft.config, paths: draft.paths };
  if (draft.type === 'mysql' || draft.type === 'postgres') {
    const { database: _single, ...rest } = draft.config;
    return {
      ...rest,
      databases: draft.databases,
      // Older builds read the single name.
      database: draft.databases[0] ?? '',
      host: draft.host || 'localhost',
      username: draft.username || undefined,
      password: draft.password || undefined,
    };
  }
  return draft.config;
}

const SOURCE_OPTIONS: Array<[BackupSourceType, string]> = [
  ['files', 'Files and folders'],
  ['mysql', 'MySQL / MariaDB database'],
  ['postgres', 'PostgreSQL database'],
  ['sqlite', 'SQLite database file'],
  ['config', 'Device configuration (over SSH)'],
  ['platform', "This platform's own database"],
];

const BROWSE = '/api/plugins/backups/browse';

/** A check list of the databases on the server, with a box for any name it cannot list. */
function DatabaseChecklist({ draft, resourceId, onChange }: { draft: SourceDraft; resourceId: string; onChange: (patch: Partial<SourceDraft>) => void }) {
  const [listed, setListed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<BackupBrowseDatabasesResponse>(`${BROWSE}/databases`, {
        type: draft.type,
        resourceId: resourceId || null,
        host: draft.host || undefined,
        username: draft.username || undefined,
        password: draft.password || undefined,
      });
      setListed(res.databases);
    } catch (err) {
      setListed(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
    // Reloaded by the button after the login changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.type, resourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (name: string) =>
    onChange({ databases: draft.databases.includes(name) ? draft.databases.filter((d) => d !== name) : [...draft.databases, name] });
  const names = [...new Set([...(listed ?? []), ...draft.databases])].sort();

  return (
    <>
      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Host</label>
          <input className="input" value={draft.host} onChange={(e) => onChange({ host: e.target.value })} placeholder="localhost" />
        </div>
        <div className="field grow">
          <label className="field-label">Database user</label>
          <input className="input" value={draft.username} onChange={(e) => onChange({ username: e.target.value })} autoComplete="off" placeholder={resourceId ? 'The server’s own root' : 'root'} />
        </div>
        <div className="field grow">
          <label className="field-label">Password</label>
          <input className="input" type="password" value={draft.password} onChange={(e) => onChange({ password: e.target.value })} autoComplete="new-password" />
        </div>
      </div>
      <div className="field">
        <div className="row" style={{ gap: 8, marginBottom: 4 }}>
          <label className="field-label grow" style={{ margin: 0 }}>
            Databases {draft.databases.length > 0 && <span className="subtle">({draft.databases.length} selected)</span>}
          </label>
          {names.length > 1 && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange({ databases: draft.databases.length === names.length ? [] : names })}>
              {draft.databases.length === names.length ? 'Clear' : 'Select all'}
            </button>
          )}
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void load()}>
            {busy ? 'Loading…' : 'Reload list'}
          </button>
        </div>
        {error && <div style={{ fontSize: 12, color: 'var(--itr-critical)', marginBottom: 6, whiteSpace: 'pre-wrap' }}>{error}</div>}
        <div style={{ maxHeight: 220, overflow: 'auto', border: 'var(--itr-border-w) solid var(--itr-border)', borderRadius: 6, padding: 6 }}>
          {names.length === 0 && <div className="subtle" style={{ fontSize: 12.5, padding: 4 }}>{busy ? 'Loading…' : 'No databases listed. Type a name below.'}</div>}
          {names.map((name) => (
            <label key={name} className="row" style={{ gap: 8, padding: '3px 4px', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={draft.databases.includes(name)} onChange={() => toggle(name)} />
              <span className="mono">{name}</span>
            </label>
          ))}
        </div>
        <div className="row" style={{ gap: 6, marginTop: 6 }}>
          <input className="input grow" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Another database, by name" />
          <button
            type="button"
            className="btn btn-sm"
            disabled={!typed.trim()}
            onClick={() => {
              if (!draft.databases.includes(typed.trim())) onChange({ databases: [...draft.databases, typed.trim()] });
              setTyped('');
            }}
          >
            Add
          </button>
        </div>
      </div>
    </>
  );
}

/** One level of the tree; a folder loads its contents when opened. */
function FolderNode({ entry, depth, resourceId, selected, covered, onToggle }: { entry: BackupBrowseEntry; depth: number; resourceId: string; selected: string[]; covered: boolean; onToggle: (path: string) => void }) {
  const [open, setOpen] = useState(depth === -1);
  const [children, setChildren] = useState<BackupBrowseEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || children) return;
    api
      .post<BackupBrowseFoldersResponse>(`${BROWSE}/folders`, { resourceId: resourceId || null, path: entry.path })
      .then((res) => setChildren(res.entries))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [open, children, entry.path, resourceId]);

  const checked = selected.includes(entry.path);
  return (
    <div>
      {depth >= 0 && (
        <div className="row" style={{ gap: 4, padding: '2px 0', paddingLeft: depth * 16, fontSize: 13 }}>
          {entry.dir ? (
            <button type="button" className="btn btn-ghost btn-icon btn-sm" style={{ width: 20, height: 20 }} onClick={() => setOpen(!open)} aria-label={open ? 'Collapse' : 'Expand'}>
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : (
            <span style={{ width: 20 }} />
          )}
          <label className="row grow" style={{ gap: 6, cursor: covered ? 'default' : 'pointer', minWidth: 0 }}>
            {/* A ticked folder takes everything under it, so its children show ticked and locked. */}
            <input type="checkbox" checked={checked || covered} disabled={covered} onChange={() => onToggle(entry.path)} />
            <span className={`mono truncate${entry.dir ? '' : ' subtle'}`}>{entry.name}{entry.dir ? '/' : ''}</span>
          </label>
        </div>
      )}
      {open && error && <div style={{ fontSize: 12, color: 'var(--itr-critical)', paddingLeft: (depth + 1) * 16 }}>{error}</div>}
      {open && !children && !error && <div className="subtle" style={{ fontSize: 12, paddingLeft: (depth + 1) * 16 + 24 }}>Loading…</div>}
      {open && children?.length === 0 && <div className="subtle" style={{ fontSize: 12, paddingLeft: (depth + 1) * 16 + 24 }}>Empty</div>}
      {open &&
        children?.map((child) => (
          <FolderNode key={child.path} entry={child} depth={depth + 1} resourceId={resourceId} selected={selected} covered={covered || checked} onToggle={onToggle} />
        ))}
    </div>
  );
}

/** The folder tree as a check list, the chosen paths under it, and a box for a path typed by hand. */
function FolderChecklist({ draft, resourceId, onChange }: { draft: SourceDraft; resourceId: string; onChange: (patch: Partial<SourceDraft>) => void }) {
  const [typed, setTyped] = useState('');
  const toggle = (path: string) => {
    if (draft.paths.includes(path)) return onChange({ paths: draft.paths.filter((p) => p !== path) });
    // Ticking a folder replaces anything already ticked inside it.
    onChange({ paths: [...draft.paths.filter((p) => !p.startsWith(`${path === '/' ? '' : path}/`)), path] });
  };
  return (
    <div className="field">
      <label className="field-label">Folders and files {draft.paths.length > 0 && <span className="subtle">({draft.paths.length} selected)</span>}</label>
      <div style={{ maxHeight: 300, overflow: 'auto', border: 'var(--itr-border-w) solid var(--itr-border)', borderRadius: 6, padding: 6 }}>
        {/* Re-rooted when the server changes: a tree from another box would be wrong. */}
        <FolderNode key={resourceId} entry={{ name: '/', path: '/', dir: true }} depth={-1} resourceId={resourceId} selected={draft.paths} covered={false} onToggle={toggle} />
      </div>
      {draft.paths.length > 0 && (
        <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {draft.paths.map((p) => (
            <button key={p} type="button" className="badge mono" style={{ cursor: 'pointer' }} title="Remove" onClick={() => onChange({ paths: draft.paths.filter((x) => x !== p) })}>
              {p} ×
            </button>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: 6, marginTop: 6 }}>
        <input className="input grow" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Another path, typed by hand" />
        <button
          type="button"
          className="btn btn-sm"
          disabled={!typed.trim()}
          onClick={() => {
            if (!draft.paths.includes(typed.trim())) onChange({ paths: [...draft.paths, typed.trim()] });
            setTyped('');
          }}
        >
          Add
        </button>
      </div>
      <span className="field-help">{resourceId ? 'Read on the chosen server over SSH.' : 'Read on this server.'} Ticking a folder takes everything inside it.</span>
    </div>
  );
}

export function PlanForm({ targets, plan, resource, onSaved }: { targets: StorageTarget[]; plan?: PlanRow; resource?: Resource; onSaved: () => void }) {
  const toast = useToast();
  const isEdit = Boolean(plan);
  const [sources, setSources] = useState<SourceDraft[]>(() => (plan ? planSources(plan).map((s) => draftOf(s.type, s.config)) : [draftOf('files', {})]));
  const [resourceId, setResourceId] = useState(plan?.resourceId ?? resource?.id ?? '');
  const resources = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 5000 }), [], { enabled: !resource });
  const patchSource = (key: number, patch: Partial<SourceDraft>) => setSources((list) => list.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const configSource = sources.find((d) => d.type === 'config');
  const [form, setForm] = useState({
    name: plan?.name ?? '',
    targetId: plan?.targetId ?? targets[0]?.id ?? '',
    offsiteTargetId: plan?.offsiteTargetId ?? '',
    scheduleCron: plan?.scheduleCron ?? '0 2 * * *',
    keepLast: String(plan?.keepLast ?? 7),
    keepDaily: String(plan?.keepDaily ?? 7),
    keepWeekly: String(plan?.keepWeekly ?? 4),
    keepMonthly: String(plan?.keepMonthly ?? 12),
    compression: plan?.compression ?? 'gzip',
    verify: plan?.verify ?? true,
    enabled: plan?.enabled ?? true,
    // Device-config plans keep the login they were created with (the
    // Protect dialog copies it in), so this is the only place to change it.
    sshUsername: configSource ? String(configSource.config.username ?? '') : '',
    sshPassword: '',
  });
  const [busy, setBusy] = useState(false);
  // A plan that reads the resource's own login has nothing to edit here.
  const usesResourceLogin = configSource?.config.credentials === 'resource';
  const hasSavedCredential = Boolean(configSource?.config.vaultEntryId);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const empty = sources.find((d) => (d.type === 'files' && d.paths.length === 0) || ((d.type === 'mysql' || d.type === 'postgres') && d.databases.length === 0));
    if (empty) {
      toast(empty.type === 'files' ? 'Choose at least one folder' : 'Choose at least one database', 'bad');
      return;
    }
    const built = sources.map((d) => ({
      type: d.type,
      config: d.type === 'config' && !usesResourceLogin ? withConfigLogin(d.config, form.sshUsername.trim(), form.sshPassword) : sourceConfigOf(d),
    }));
    setBusy(true);
    try {
      await api.post('/api/plugins/backups/plans', {
        // Round-trip anything this form has no field for (resourceId, and a
        // 'config'/'platform' plan's own vendor-specific sourceConfig) rather
        // than defaulting it away — this same endpoint is a full upsert, so an
        // omitted field is a silent data loss on save, not a no-op.
        ...plan,
        id: plan?.id,
        name: form.name.trim(),
        // The first source doubles as the plan's own, for older app builds.
        sourceType: built[0]!.type,
        sourceConfig: built[0]!.config,
        sources: built,
        resourceId: resourceId || null,
        customerId: resource?.customerId ?? resources.data?.find((r) => r.id === resourceId)?.customerId ?? plan?.customerId ?? null,
        targetId: form.targetId || null,
        offsiteTargetId: form.offsiteTargetId || null,
        scheduleCron: form.scheduleCron,
        compression: form.compression,
        verify: form.verify,
        enabled: form.enabled,
        keepLast: Number(form.keepLast),
        keepDaily: Number(form.keepDaily),
        keepWeekly: Number(form.keepWeekly),
        keepMonthly: Number(form.keepMonthly),
      });
      onSaved();
    } catch (err) {
      toast(isEdit ? 'Could not save the plan' : 'Could not create the plan', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Plan name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="Nightly config backup" />
      </div>

      {!resource && (
        <div className="field">
          <label className="field-label">Server to read from</label>
          <select className="select" value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
            <option value="">This server</option>
            {resourceId && !resources.data?.some((r) => r.id === resourceId) && <option value={resourceId}>The plan's resource</option>}
            {(resources.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <span className="field-help">Folders and databases are listed and backed up on this server, over its SSH login.</span>
        </div>
      )}

      {sources.map((draft, index) => (
        <div key={draft.key} style={sources.length > 1 ? { border: 'var(--itr-border-w) solid var(--itr-border)', borderRadius: 8, padding: 10, marginBottom: 12 } : undefined}>
          <div className="field">
            <div className="row" style={{ gap: 8, marginBottom: 4 }}>
              <label className="field-label grow" style={{ margin: 0 }}>
                {sources.length > 1 ? `What to back up (${index + 1} of ${sources.length})` : 'What to back up'}
              </label>
              {sources.length > 1 && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSources((list) => list.filter((d) => d.key !== draft.key))}>
                  Remove
                </button>
              )}
            </div>
            <select
              className="select"
              value={draft.type}
              onChange={(e) => {
                const next = draftOf(e.target.value as BackupSourceType, {});
                setSources((list) => list.map((d) => (d.key === draft.key ? { ...next, key: d.key } : d)));
              }}
            >
              {SOURCE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {draft.type === 'files' && <FolderChecklist draft={draft} resourceId={resourceId} onChange={(patch) => patchSource(draft.key, patch)} />}
          {(draft.type === 'mysql' || draft.type === 'postgres') && (
            <DatabaseChecklist key={`${draft.type}:${resourceId}`} draft={draft} resourceId={resourceId} onChange={(patch) => patchSource(draft.key, patch)} />
          )}
        </div>
      ))}
      <div style={{ marginBottom: 16 }}>
        <button type="button" className="btn btn-sm" onClick={() => setSources((list) => [...list, draftOf(list.some((d) => d.type === 'files') ? 'mysql' : 'files', {})])}>
          <Plus size={13} /> Add another source
        </button>
        <span className="field-help" style={{ display: 'block' }}>One plan can hold a database and files together; they end up in one archive.</span>
      </div>

      {configSource && isEdit && !usesResourceLogin && (
        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">Login username</label>
            <input className="input" value={form.sshUsername} onChange={set('sshUsername')} autoComplete="off" placeholder="root" />
          </div>
          <div className="field grow">
            <label className="field-label">Login password</label>
            <input
              className="input"
              type="password"
              value={form.sshPassword}
              onChange={set('sshPassword')}
              autoComplete="new-password"
              placeholder={hasSavedCredential ? 'Using a saved credential' : 'Unchanged'}
            />
          </div>
        </div>
      )}
      {configSource && isEdit && usesResourceLogin && (
        <span className="field-help" style={{ display: 'block', marginBottom: 16 }}>
          Logs in with the SSH login set on the resource — change it there.
        </span>
      )}

      <div className="field">
        <label className="field-label">Storage target</label>
        <select className="select" value={form.targetId} onChange={set('targetId')} required>
          <option value="">Select a target…</option>
          {targets.map((target) => (
            <option key={target.id} value={target.id}>
              {target.name} ({target.type})
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Offsite copy</label>
        <select className="select" value={form.offsiteTargetId} onChange={set('offsiteTargetId')}>
          <option value="">None</option>
          {targets
            .filter((target) => target.id !== form.targetId)
            .map((target) => (
              <option key={target.id} value={target.id}>
                {target.name} ({target.type})
              </option>
            ))}
        </select>
        <span className="field-help">
          Every successful archive is also copied here, and pruned by the same retention. If the copy fails the backup still
          counts, and a separate alert says the offsite copy is missing.
        </span>
      </div>

      <div className="field">
        <label className="field-label">Schedule (cron)</label>
        <input className="input mono" value={form.scheduleCron} onChange={set('scheduleCron')} placeholder="0 2 * * *" />
        <span className="field-help">Minute hour day month weekday. Default runs at 02:00 every night.</span>
      </div>

      <div className="field">
        <label className="field-label">Retention</label>
        <div className="row" style={{ gap: 8 }}>
          {(
            [
              ['keepLast', 'Last'],
              ['keepDaily', 'Daily'],
              ['keepWeekly', 'Weekly'],
              ['keepMonthly', 'Monthly'],
            ] as const
          ).map(([key, label]) => (
            <div key={key} style={{ flex: 1 }}>
              <input className="input" type="number" min={0} value={form[key]} onChange={set(key)} />
              <div className="subtle" style={{ fontSize: 11, textAlign: 'center', marginTop: 3 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
        <span className="field-help">
          Grandfather-father-son. An archive is kept if any tier still wants it, so the monthly copy survives even once the
          daily window has moved past it.
        </span>
      </div>

      <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={form.verify} onChange={set('verify')} />
        <span style={{ fontSize: 13 }}>Checksum-verify each archive after writing</span>
      </label>

      <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
        <span style={{ fontSize: 13 }}>Enabled</span>
      </label>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create plan'}
      </button>
    </form>
  );
}

function TargetForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    type: 'local',
    path: '',
    host: '',
    port: '',
    secure: false,
    domain: '',
    username: '',
    password: '',
    warnPct: '85',
  });
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/plugins/backups/targets', {
        name: form.name.trim(),
        type: form.type,
        config: {
          path: form.path,
          host: form.host || undefined,
          port: (form.type === 'ftp' || form.type === 'smb') && form.port ? Number(form.port) : undefined,
          secure: form.type === 'ftp' ? form.secure : undefined,
          domain: form.type === 'smb' ? form.domain || undefined : undefined,
          username: form.username || undefined,
          password: form.password || undefined,
          warnPct: Number(form.warnPct),
        },
      });
      onSaved();
    } catch (err) {
      toast('Could not add the target', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const hostLabel =
    form.type === 's3' ? 'Endpoint' : form.type === 'webdav' ? 'WebDAV base URL' : form.type === 'smb' ? 'Server (blank if already mounted)' : 'Host / endpoint';
  const hostPlaceholder =
    form.type === 'webdav'
      ? 'https://cloud.example.com/remote.php/dav/files/admin'
      : form.type === 'ftp' || form.type === 'smb'
        ? 'file-server.example.com'
        : 'backup.example.com';

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="NAS — backup volume" />
      </div>

      <div className="field">
        <label className="field-label">Type</label>
        <select className="select" value={form.type} onChange={set('type')}>
          <option value="local">Local or mounted path</option>
          <option value="sftp">SFTP</option>
          <option value="ftp">FTP / FTPS</option>
          <option value="webdav">WebDAV</option>
          <option value="s3">S3-compatible</option>
          <option value="smb">SMB / CIFS / Windows share</option>
          <option value="nfs">NFS (mounted)</option>
        </select>
      </div>

      <div className="field">
        <label className="field-label">{form.type === 's3' ? 'Bucket' : 'Path'}</label>
        <input
          className="input"
          required
          value={form.path}
          onChange={set('path')}
          placeholder={
            form.type === 's3' ? 's3://my-backups' : form.type === 'webdav' ? 'backups (subfolder, optional)' : form.type === 'smb' ? 'share/subfolder' : '/mnt/backups'
          }
        />
        {form.type === 'smb' && (
          <span className="field-help">
            First segment is the share name (e.g. "backups"); anything after it is a subfolder within that share. Leave the server field below
            blank if this is instead a path already mounted by the OS.
          </span>
        )}
      </div>

      {form.type !== 'local' && (
        <>
          <div className="field">
            <label className="field-label">{hostLabel}</label>
            <input className="input" value={form.host} onChange={set('host')} placeholder={hostPlaceholder} />
          </div>
          {(form.type === 'ftp' || form.type === 'smb') && (
            <div className="field">
              <label className="field-label">Port</label>
              <input
                className="input"
                type="number"
                min={1}
                max={65535}
                value={form.port}
                onChange={set('port')}
                placeholder={form.type === 'smb' ? '445' : '21'}
              />
            </div>
          )}
          {form.type === 'smb' && (
            <div className="field">
              <label className="field-label">Domain (optional)</label>
              <input className="input" value={form.domain} onChange={set('domain')} placeholder="WORKGROUP" />
            </div>
          )}
          <div className="field">
            <label className="field-label">{form.type === 's3' ? 'Access key' : 'Username'}</label>
            <input className="input" value={form.username} onChange={set('username')} autoComplete="off" />
          </div>
          <div className="field">
            <label className="field-label">{form.type === 's3' ? 'Secret key' : 'Password'}</label>
            <input className="input" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
            <span className="field-help">Encrypted at rest with the server's secrets key.</span>
          </div>
          {form.type === 'ftp' && (
            <label className="row" style={{ gap: 8, marginBottom: 14, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.secure}
                onChange={(e) => setForm((f) => ({ ...f, secure: e.target.checked }))}
              />
              Use FTPS (explicit TLS)
            </label>
          )}
        </>
      )}

      <div className="field">
        <label className="field-label">Warn at % full</label>
        <input className="input" type="number" min={1} max={100} value={form.warnPct} onChange={set('warnPct')} />
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Adding…' : 'Add storage target'}
      </button>
    </form>
  );
}
